import { api, type RendersResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Fila de render.
 *
 * A tela tem UM destaque, e ele não é a contagem total: é o estado `ambiguous`.
 * Esse é o único em que existe dinheiro comprometido sem entrega confirmada, e
 * o único que não se resolve sozinho — repetir o pedido pagaria duas vezes pelo
 * mesmo vídeo, então o caminho é reconciliar.
 *
 * Formatação de dinheiro à mão, pela mesma razão de sempre: `Intl` varia com a
 * versão do ICU e o mesmo valor sairia diferente em máquinas diferentes.
 */

const STATE_LABEL: Record<string, string> = {
  reserved: 'Custo reservado, nada despachado',
  dispatched: 'Despachado',
  processing: 'Processando no provedor',
  succeeded: 'Concluído',
  failed: 'Falhou',
  ambiguous: 'Estado remoto DESCONHECIDO',
  cancelled: 'Cancelado',
};

const STATE_CLASS: Record<string, string> = {
  ambiguous: 'border-l-4 border-l-danger bg-danger-surface/60',
  failed: 'border-l-4 border-l-warn',
  succeeded: 'border-l-4 border-l-ok',
};

/**
 * Centavos para reais, escrito à mão.
 *
 * Centavos exatos omitem os decimais; o resto aparece com dois dígitos, nunca
 * arredondado. E o espaço depois do símbolo é um espaço COMUM — `Intl` insere
 * um não-quebrável, que quebra comparação de string sem que ninguém veja.
 */
export function formatCents(cents: number): string {
  const negativo = cents < 0;
  const abs = Math.abs(cents);
  const reais = Math.floor(abs / 100);
  const resto = abs % 100;

  const inteiro = String(reais).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const texto = resto === 0 ? inteiro : `${inteiro},${String(resto).padStart(2, '0')}`;
  return `${negativo ? '-' : ''}R$ ${texto}`;
}

export function Renders() {
  const resource = useResource<RendersResponse>(() => api.renders(), []);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Digital Twin (HeyGen)</h1>
        <p className="mt-2 text-ink-muted">Carregando…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Digital Twin (HeyGen)</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar a fila de render.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Isto não significa que não há renders pendentes de reconciliação. Significa que não
            sabemos. Código: {resource.code}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Digital Twin (HeyGen)</h1>
        <p className="mt-1 text-ink-muted">{formatNumber(data.total)} trabalho(s) recentes.</p>
      </header>

      {data.needs_reconciliation > 0 ? (
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">
            {formatNumber(data.needs_reconciliation)} render(s) com estado remoto desconhecido
          </p>
          <p className="mt-1 text-xs">
            Existe custo reservado sem entrega confirmada. Repetir o pedido pagaria duas vezes pelo
            mesmo vídeo. O caminho é reconciliar pela chave de idempotência, que é leitura e
            portanto segura de repetir.
          </p>
        </div>
      ) : null}

      {data.total === 0 ? (
        <p className="mt-6 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhum render pedido ainda. Produção paga só acontece depois da aprovação semântica.
        </p>
      ) : (
        <ul className="mt-6 space-y-3">
          {data.jobs.map((j) => (
            <li
              key={j.job_id}
              className={`rounded-card border border-line bg-card p-4 shadow-card ${STATE_CLASS[j.state] ?? ''}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{STATE_LABEL[j.state] ?? j.state}</span>
                <span className="text-xs text-ink-muted">
                  {j.provider} · {formatCents(j.reserved_cents)} reservado
                </span>
              </div>

              <p className="mt-1 text-xs text-ink-faint">
                Ativo {j.asset_id} · {formatNumber(j.attempts)} tentativa(s) ·{' '}
                {j.remote_job_id ? `id remoto ${j.remote_job_id}` : 'sem id remoto'}
              </p>

              {j.failure_reason ? (
                <p className="mt-2 text-xs text-warn-ink">
                  {j.failure_code ? `${j.failure_code}: ` : ''}
                  {j.failure_reason}
                </p>
              ) : null}

              {j.detected_artifacts.length > 0 ? (
                <p className="mt-2 text-xs text-warn-ink">
                  Artefatos detectados: {j.detected_artifacts.join(', ')}. O QA técnico reprova o
                  ativo até serem resolvidos.
                </p>
              ) : null}

              <p className="mt-2 text-xs text-ink-faint">
                Pedido em {formatInstant(j.created_at)}
                {j.completed_at ? ` · concluído em ${formatInstant(j.completed_at)}` : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
